"use client";

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AppLayout from "../../../navigation/Layout/Layout";

import CycleOverview      from "../../components/CyclePage/CycleOverview/CycleOverview";
import CycleInsightsPanel from "../../components/CyclePage/CycleInsightsPanel/CycleInsightsPanel";
import CycleTrackerModal  from "../../components/CyclePage/CycleTrackerModal/CycleTrackerModal";
import CycleTrackTab      from "../../components/CyclePage/CycleTrackTab/CycleTrackTab";
import CycleInsightsTab   from "../../components/CyclePage/CycleInsightsTab/CycleInsightsTab";
import CycleChatBubble    from "../../components/CyclePage/CycleChatBubble/CycleChatBubble";

import useCycleBot from "../../hooks/useCycleBot";
import { selectAuthUser, selectIsAuthenticated } from "../../../authPatient/redux/selectors/authSelectors";
import {
  selectEffectiveUser,
  selectEffectiveUserId,
} from "../../../sous-profile/redux/selectors/sousProfileSelectors";

import "./Cycle.css";

const STORAGE_KEY  = "cycle_tracker_settings_v2";
const NOW_OVERRIDE = "2026-01-20";

const getTheme = () =>
  document.documentElement?.getAttribute("data-theme") ||
  document.body?.getAttribute("data-theme") || "light";

const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M15.8805 5.10929C16.277 4.71285 16.4998 4.17514 16.4999 3.61443C16.4999 3.05372 16.2773 2.51594 15.8808 2.11941C15.4844 1.72288 14.9467 1.50007 14.386 1.5C13.8253 1.49993 13.2875 1.7226 12.8909 2.11904L2.88145 12.1308C2.70732 12.3044 2.57854 12.5182 2.50645 12.7533L1.5157 16.0173C1.49632 16.0821 1.49485 16.151 1.51146 16.2167C1.52807 16.2823 1.56214 16.3422 1.61005 16.39C1.65796 16.4379 1.71792 16.4718 1.78357 16.4883C1.84922 16.5048 1.91812 16.5033 1.98295 16.4838L5.2477 15.4938C5.48258 15.4224 5.69633 15.2944 5.8702 15.121L15.8805 5.10929Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.25 3.75L14.25 6.75" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function Cycle() {
  const { t, i18n } = useTranslation("cycle");
  const user            = useSelector(selectAuthUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const lang = (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const cycleFallback = lang.startsWith("ar")
    ? "دورتي"
    : lang.startsWith("en")
      ? "My Cycle"
      : "Mon Cycle";
  const TABS = [
    {
      key: "overview",
      label: t("cycle.page.tabs.overview"),
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
    },
    {
      key: "track",
      label: t("cycle.page.tabs.track"),
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      ),
    },
    {
      key: "insights",
      label: t("cycle.page.tabs.insights"),
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6"  y1="20" x2="6"  y2="14" />
        </svg>
      ),
    },
  ];
  
  // État NestJS complet pour le Dashboard
  const store_state_ref = useSelector((s) => ({
    cycle: {
      // NestJS predictions = Array depuis GET /cycles/:id/predictions
      predictions: s.cycle?.predictions || s.cycle?.predictionsList || [],
      summary:     s.cycle?.summary     || s.cycle?.cycleSummary    || null,
    }
  }));

  // ── Fenêtre pré-menstruelle (7 jours avant J1 prédit) ──────────────────────
  const premenstrualWindow = React.useMemo(() => {
    const cycleState  = store_state_ref?.cycle || {};
    const predsArray  = Array.isArray(cycleState.predictions) ? cycleState.predictions : [];
    // Prendre la première prédiction FUTURE (>= aujourd'hui)
    const today0 = new Date(); today0.setHours(0,0,0,0);
    const futurePred = predsArray.find((p) => {
      const d = new Date(p.predictedStartDate);
      d.setHours(0,0,0,0);
      return d >= today0;
    });
    const nextPredRaw = futurePred?.predictedStartDate || null;
    if (!nextPredRaw) return null;

    const today    = new Date();
    today.setHours(0, 0, 0, 0);
    const nextJ1   = new Date(nextPredRaw);
    nextJ1.setHours(0, 0, 0, 0);
    const daysLeft = Math.ceil((nextJ1 - today) / 86400000);

    // Fenêtre active : entre J-7 et J0
    const inWindow = daysLeft >= 0 && daysLeft <= 7;
    // Rapport déclenché : J1 atteint (daysLeft === 0)
    const reportDue = daysLeft === 0;
    // Numéro du jour dans la fenêtre (Jour 1 = 7 jours avant, Jour 7 = J1)
    const dayInWindow = inWindow ? 8 - daysLeft : null;

    return {
      nextJ1Date:  nextPredRaw,
      daysLeft,
      inWindow,
      reportDue,
      dayInWindow, // 1..7
      windowStart: new Date(nextJ1.getTime() - 7 * 86400000)
        .toISOString().split("T")[0],
    };
  }, [store_state_ref]);

  // Cycles NestJS existants (state.cycle) — pour l'onglet Mes Données
  const nestCycles = useSelector((s) => {
    // Cherche dans toutes les clés possibles de state.cycle
    const raw = s.cycle?.cycles
             || s.cycle?.data
             || s.cycle?.list
             || s.cycle?.items
             || (Array.isArray(s.cycle) ? s.cycle : []);

    if (!Array.isArray(raw) || raw.length === 0) return [];

    // Log une seule fois pour voir la structure réelle
    if (raw[0]) {}

    return raw.map((c, i) => {
      // Champs réels NestJS (confirmés via console log)
      const cycleLen  = c.cycleDurationInDays; // durée totale du cycle
      const bleedLen  = c.cycleLengthInDays;   // durée des saignements

      return {
        cycle_number:  i + 1,
        start_date:    c.startDate,
        end_date:      c.endDate,
        cycle_length:  cycleLen  ? `${cycleLen}j`  : null,
        bleeding_days: bleedLen  ? `${bleedLen}j`  : null,
        status:        c.isPredicted ? "predicted" : (c.isAbnormal ? "abnormal" : "normal"),
        medical_note:  c.notes,
      };
    });
  });

  const [activeTab,     setActiveTab]     = useState("overview");
  const [isEditOpen,    setIsEditOpen]    = useState(false);
  const [cycleSettings, setCycleSettings] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [showSearch,    setShowSearch]    = useState(false);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [showNotifs,    setShowNotifs]    = useState(false);
  const searchInputRef = useRef(null);
  const notifRef       = useRef(null);

  /* ── Close notification dropdown on outside click ── */
  useEffect(() => {
    if (!showNotifs) return;
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNotifs]);

  /* ── Auto-focus search input when opened ── */
  useEffect(() => {
    if (showSearch) searchInputRef.current?.focus();
    else setSearchQuery("");
  }, [showSearch]);

  /* ── useCycleBot — tous les appels API ── */
  const {
    health, fetchHealth,
    chatSession, messages, isTyping, initChat, sendUserMessage, resetChat,
    chatLimit,
    premenstrualReport, fetchPMReport,
    journalSummary, journalAnalysis, journalEntries, logResult,
    logEntry, fetchJournalSummary, fetchJournalAnalysis, fetchJournalEntries,
    flowsList, flowSession, flowReport, isGeneratingReport,
    fetchFlows, beginFlow, answerFlow, cancelFlow,
  } = useCycleBot({ autoInit: true });

  /* ── Background theming ── */
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const header = document.querySelector(".app-header-container");
    const mainContent = document.querySelector(".app-main-content");
    const els  = [
      document.getElementById("root"),
      document.querySelector(".app-layout"),
      document.querySelector(".app-body"),
      document.querySelector(".app-layout__content"),
      document.querySelector(".app-sider"),
    ];
    const applyBg = () => {
      const theme = getTheme();
      const bg = theme === "dark"
        ? "radial-gradient(circle at top, #020617 0%, #020617 100%)"
        : "#ffffff";
      const headerBg = theme === "dark"
        ? "linear-gradient(180deg,#020617 0%)"
        : "#ffffff";
      [body, html].forEach((el) => {
        el.style.setProperty("background", bg, "important");
        el.style.setProperty("background-attachment", "fixed", "important");
      });
      els.forEach((el) => el?.style.setProperty("background", bg, "important"));
      header?.style.setProperty("background", headerBg, "important");
      mainContent?.style.setProperty("background", bg, "important");
    };
    applyBg();
    const obs = new MutationObserver(applyBg);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    obs.observe(document.body,            { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      obs.disconnect();
      [body, html].forEach((el) => {
        el.style.removeProperty("background");
        el.style.removeProperty("background-attachment");
      });
      els.forEach((el) => el?.style.removeProperty("background"));
      header?.style.removeProperty("background");
      mainContent?.style.removeProperty("background");
    };
  }, []);

  /* ── Cycle settings (localStorage) ── */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.lastPeriodStart) return;
      setCycleSettings({
        lastPeriodStart: parsed.lastPeriodStart,
        cycleLength:  Number(parsed.cycleLength  || 28),
        periodLength: Number(parsed.periodLength || 5),
      });
    } catch { /* ignore */ }
  }, []);

  const handleSave = useCallback((payload) => {
    const next = {
      lastPeriodStart: payload.lastPeriodStart,
      cycleLength:  Number(payload.cycleLength  || 28),
      periodLength: Number(payload.periodLength || 5),
    };
    setCycleSettings(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
    setIsEditOpen(false);
  }, []);

  /* ── Lazy load par onglet (premier accès uniquement) ── */
  const visitedTabs = useRef(new Set(["cycle", "dashboard", "conversation"]));

  useEffect(() => {
    if (!isAuthenticated || visitedTabs.current.has(activeTab)) return;
    visitedTabs.current.add(activeTab);

    switch (activeTab) {
      case "flows":
        fetchFlows();
        break;
      case "journal":
        fetchJournalSummary();
        fetchJournalAnalysis();
        fetchJournalEntries(30);
        break;
      case "info":
        fetchJournalEntries(90);
        break;
      default:
        break;
    }
  }, [activeTab, isAuthenticated]);

  /* ── Adapters shape → composants ── */
  const dashboardData = (() => {
    // ── NestJS predictions = Array → prendre [0] = prochain cycle
    const cycleState  = store_state_ref?.cycle || {};
    const predsArray  = Array.isArray(cycleState.predictions) ? cycleState.predictions : [];
    const nextPred    = predsArray[0] || null;   // prochain cycle prédit

    // ── Dernier cycle réel
    const lastC = nestCycles[0] || null;

    // ── health_score depuis chatSession.data (back le calcule à 53/100)
    const csData = chatSession.data || {};
    const hsRaw  = csData.health_score;
    const hsScore = typeof hsRaw === "number" ? hsRaw
                  : typeof hsRaw?.score === "number" ? hsRaw.score
                  : null;
    const hsMsg   = hsRaw?.message || csData.health_message || null;

    // ── Calcule daysUntil depuis predictedStartDate
    let daysUntil = null;
    let progress  = null;
    if (nextPred?.predictedStartDate) {
      const diff = Math.ceil((new Date(nextPred.predictedStartDate) - new Date()) / 86400000);
      daysUntil = diff > 0 ? diff : 0;
    }
    if (lastC?.start_date && nextPred?.predictedStartDate) {
      const total   = Math.ceil((new Date(nextPred.predictedStartDate) - new Date(lastC.start_date)) / 86400000);
      const elapsed = Math.ceil((new Date() - new Date(lastC.start_date)) / 86400000);
      progress = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;
    }

    const alerts = Array.isArray(csData.alerts) ? csData.alerts : [];

    return {
      last_cycle: lastC ? {
        start_date:    lastC.start_date,
        end_date:      lastC.end_date,
        cycle_length:  lastC.cycle_length,
        bleeding_days: lastC.bleeding_days,
        medical_note:  lastC.medical_note,
      } : null,

      predictions: {
        next_cycle: {
          start:     nextPred?.predictedStartDate || null,
          daysUntil,
          progress,
        },
        fertile_window: {
          start: nextPred?.fertileWindow?.start || null,
          end:   nextPred?.fertileWindow?.end   || null,
        },
        fertility_status: nextPred ? "Fenêtre calculée" : null,
      },

      health_score: { score: hsScore, message: hsMsg },

      stats: {
        cycles_count:     nestCycles.length || null,
        avg_cycle_length: cycleState.summary?.averageCycleLength ?? null,
        alerts_count:     alerts.length,
        regularity_score: cycleState.summary?.regularityScore ?? null,
      },

      alerts,
    };
  })();

  const handleSearch = useCallback(async (date) => {
    const res = await fetchJournalEntries(365);
    const all = res?.entries || journalEntries.data || [];
    setSearchResults(all.filter((e) => e.entry_date?.startsWith(date.slice(0, 7))));
  }, [fetchJournalEntries, journalEntries.data]);

  /* ── Search: quick-navigate to matching tab ── */
  const handleSearchNavigate = useCallback((tabKey) => {
    setActiveTab(tabKey);
    setShowSearch(false);
  }, []);

  const filteredTabs = useMemo(() =>
    TABS.filter((tab) =>
      tab.label.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  [searchQuery, TABS]);

  /* ── Notifications: build list from live data ── */
  const notifications = useMemo(() => {
    const list = [];
    if (premenstrualWindow) {
      if (premenstrualWindow.inWindow) {
        list.push({
          id: "pmw",
          type: "warning",
          icon: "🌸",
          text: t("cycleBot.dashboard.premenstrualWindowDay", { day: premenstrualWindow.dayInWindow }),
        });
      } else if (premenstrualWindow.daysLeft <= 10) {
        list.push({
          id: "upcoming",
          type: "info",
          icon: "📅",
          text: t("cycleBot.dashboard.nextPeriodIn", { count: premenstrualWindow.daysLeft }),
        });
      }
    }
    const alerts = dashboardData?.alerts || [];
    alerts.forEach((a, i) => {
      list.push({
        id: `alert-${i}`,
        type: "alert",
        icon: "⚠️",
        text: a.message || a.description || String(a),
      });
    });
    return list;
  }, [premenstrualWindow, dashboardData, t]);

  const modalMode = cycleSettings?.lastPeriodStart ? "edit" : "first";

  return (
    <AppLayout>
      <CycleChatBubble
        messages={messages}
        isTyping={isTyping}
        onSend={sendUserMessage}
        onNewConversation={resetChat}
        chatLimit={chatLimit}
      />
      <div className="cycle-page-bg-wrapper" />

      <div className="cycle-page">

        {/* ════ PAGE HEADER ════ */}
        <div className="cycle-page__header">
          <div className="cycle-page__header-left">
            <h1 className="cycle-page__title">{t("cycle.page.title")}</h1>
            <p className="cycle-page__subtitle">{t("cycle.page.subtitle")}</p>
          </div>
          <div className="cycle-page__header-actions">
            {/* ── Search ── */}
            <button
              type="button"
              className={`cycle-page__icon-btn${showSearch ? " cycle-page__icon-btn--active" : ""}`}
              aria-label={t("cycle.page.searchAria")}
              onClick={() => setShowSearch((s) => !s)}
            >
              {showSearch ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              )}
            </button>

            {/* ── Notifications ── */}
            <div className="cycle-page__notif-wrapper" ref={notifRef}>
              <button
                type="button"
                className={`cycle-page__icon-btn cycle-page__icon-btn--notif${showNotifs ? " cycle-page__icon-btn--active" : ""}`}
                aria-label={t("cycle.page.notificationsAria")}
                onClick={() => setShowNotifs((s) => !s)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {notifications.length > 0 && (
                  <span className="cycle-page__notif-dot">
                    {notifications.length > 9 ? "9+" : notifications.length}
                  </span>
                )}
              </button>

              {/* Notification dropdown */}
              {showNotifs && (
                <div className="cycle-notif-dropdown">
                  <div className="cycle-notif-dropdown__header">
                    <span>{t("cycle.page.notificationsAria")}</span>
                    {notifications.length > 0 && (
                      <span className="cycle-notif-dropdown__count">{notifications.length}</span>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="cycle-notif-dropdown__empty">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                      </svg>
                      <p>{t("cycleBot.dataView.noAlerts")}</p>
                    </div>
                  ) : (
                    <ul className="cycle-notif-dropdown__list">
                      {notifications.map((n) => (
                        <li key={n.id} className={`cycle-notif-item cycle-notif-item--${n.type}`}>
                          <span className="cycle-notif-item__icon">{n.icon}</span>
                          <span className="cycle-notif-item__text">{n.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ════ SEARCH BAR ════ */}
        {showSearch && (
          <div className="cycle-searchbar">
            <svg className="cycle-searchbar__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="cycle-searchbar__input"
              placeholder={t("cycle.page.searchAria") + "..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setShowSearch(false);
                if (e.key === "Enter" && filteredTabs.length > 0) {
                  handleSearchNavigate(filteredTabs[0].key);
                }
              }}
            />
            {searchQuery && (
              <div className="cycle-searchbar__results">
                {filteredTabs.length === 0 ? (
                  <div className="cycle-searchbar__empty">{t("cycleInsights.noDataYet")}</div>
                ) : (
                  filteredTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`cycle-searchbar__result${activeTab === tab.key ? " active" : ""}`}
                      onClick={() => handleSearchNavigate(tab.key)}
                    >
                      <span className="cycle-searchbar__result-icon">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* ════ TAB BAR ════ */}
        <div className="cycle-tabbar">
          <div className="cycle-tabbar__inner">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`cycle-tab ${activeTab === tab.key ? "cycle-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span className="cycle-tab__icon">{tab.icon}</span>
                <span className="cycle-tab__label">{tab.label}</span>
                {activeTab === tab.key && <span className="cycle-tab__bar" />}
              </button>
            ))}
          </div>

          {/* Point de santé service — clique pour rafraîchir */}
          <div
            className={`cycle-health-dot ${health.data?.status === "ok" ? "ok" : health.error ? "err" : "idle"}`}
            title={health.data ? `CycleBot : ${health.data.cyclebot_status?.status || health.data.status}` : "Vérification du service..."}
            onClick={fetchHealth}
          />

          {activeTab === "overview" && (
            <button type="button" className="cycle-edit-fab" onClick={() => setIsEditOpen(true)}>
              <PencilIcon />
            </button>
          )}
        </div>

        {/* ════ VUE : OVERVIEW ════ */}
        {activeTab === "overview" && (
          <div className="cycle-container">
            <div className="cycle-left"><CycleOverview /></div>
            <div className="cycle-right">
              <CycleInsightsPanel
                lastPeriodStart={cycleSettings?.lastPeriodStart || null}
                cycleLength={cycleSettings?.cycleLength || null}
                periodLength={cycleSettings?.periodLength || null}
              />
            </div>
          </div>
        )}

        {/* ════ VUE : TRACK ════ */}
        {activeTab === "track" && (
          <div className="cycle-bot-view">
            <CycleTrackTab
              cycleSettings={cycleSettings}
              flows={flowsList.data || []}
              flowsLoading={flowsList.loading}
              onStartFlow={beginFlow}
              onLogPeriod={() => setIsEditOpen(true)}
              /* CycleBot view props */
              dashboardData={dashboardData}
              premenstrualWindow={premenstrualWindow}
              nestCycles={nestCycles}
              searchResults={searchResults}
              onSearch={handleSearch}
              messages={messages}
              isTyping={isTyping}
              onSend={sendUserMessage}
              onNewConversation={resetChat}
              chatLimit={chatLimit}
              journalSummary={journalSummary.data}
              journalEntries={journalEntries.data || []}
              journalEntriesLoading={journalEntries.loading}
              onLogEntry={logEntry}
              premenstrualReport={premenstrualReport.data}
              premenstrualReportLoading={premenstrualReport.loading}
              onRequestPMReport={fetchPMReport}
              activeFlowSession={flowSession.data}
              flowReport={flowReport.data}
              isGeneratingReport={isGeneratingReport}
              onAnswer={answerFlow}
              onCancelFlow={cancelFlow}
            />
          </div>
        )}

        {/* ════ VUE : INSIGHTS ════ */}
        {activeTab === "insights" && (
          <div className="cycle-bot-view">
            <CycleInsightsTab
              stats={dashboardData.stats}
              cycles={nestCycles}
              alerts={dashboardData.alerts}
            />
          </div>
        )}

        {/* ════ VUE : JOURNAL ════
            POST /journal/log
            GET  /journal/summary
            GET  /journal/analysis
            GET  /journal/entries?days=                   */}
        {activeTab === "journal" && (
          <div className="cycle-bot-view">
            <JournalView
              journalData={journalSummary.data}
              summaryLoading={journalSummary.loading}
              analysisData={journalAnalysis.data}
              analysisLoading={journalAnalysis.loading}
              entries={journalEntries.data}
              entriesLoading={journalEntries.loading}
              premenstrualWindow={premenstrualWindow}
              pmReport={premenstrualReport.data}
              pmReportLoading={premenstrualReport.loading}
              onRequestPMReport={() => {
                const userId = user?.id || user?.userId || user?.sub;
                const nextJ1 = premenstrualWindow?.nextJ1Date;
                if (userId) fetchPMReport({ userId, nextJ1Date: nextJ1 });
              }}
              onLogEntry={async (payload) => {
                const result = await logEntry(payload);
                // Force rechargement des entrées après log réussi
                visitedTabs.current.delete("journal");
                fetchJournalEntries(30);
                fetchJournalSummary();
                return result;
              }}
              logLoading={logResult.loading}
              logError={logResult.error}
              onLoadEntries={fetchJournalEntries}
            />
          </div>
        )}

        <CycleTrackerModal
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSave={handleSave}
          initialValues={cycleSettings}
          mode={modalMode}
          nowOverride={NOW_OVERRIDE}
        />
      </div>
    </AppLayout>
  );
}
